import { useEffect, useMemo, useState, useRef } from 'react';
import {
  ErrorVariants,
  Line,
  OfflineTaskType,
  OrderLine,
  ProductVariant,
  UOM,
  addBulkItemsToCart,
  clearCartByCustomer,
  dispatch,
  getCartByCustomer,
  getCatalogConfig,
  getCurrentCustomer,
  getWorkspaceId,
  removeItemFromCart,
  showToast,
  updateItemInCart,
  useAddItemToActiveOrderMutation,
  useAppSelector,
  useCheckoutOrdersMutation,
  useNotifyToBuyerMutation,
  OrderLevelScheme,
  useRemoveOrderPromotionMutation,
  useGetOrderLevelSchemesMutation,
  useEditOrderMutation,
  GetOrderLevelSchemesResponse,
  useLazyListCartQuery,
  useLazyGetCartQuery,
} from '@zonofi/zono-money-store';
import { ReactComponent as ArrowRight } from '@zonofi/svg-icons/arrow_right.svg';
import './offline-cart-screen-content.scss';
import { PNGS, SVGS } from '../../assets';
import { useNavigate, useParams } from 'react-router-dom';
import { OrderProductActionType } from '../../constants';
import { useNetworkContext } from '../../network';
import { CartSyncAndCheckout } from '../cart-footer-button/cart-sync-and-checkout';
import { addTaskToQueue, removeTaskFromQueue } from '../../queue/offline';
import { OrderSummaryCard } from '../cart-card/order-summary-card';
import { isEmpty } from 'lodash';
import { EmptyStateView } from '@zonofi/seller-hub/components';
import { CustomerProductCard } from '../../product';
import { SelectUOMDrawer, useOrderDetailsApi } from '../../order';
import { orderHasOutOfStockLine } from '../../helper/is-order-line-out-of-stock';
import { PromotionsModal } from '../promotions-modal/promotions-modal';

export const OfflineCartScreenContent: React.FC = () => {
  const navigate = useNavigate();
  const { customerId } = useParams();
  const currentCustomer = useAppSelector(getCurrentCustomer);
  const { networkStatus } = useNetworkContext();
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);
  const [getCartDetails, cartAPIResponse] = useLazyGetCartQuery();
  const cartData = useAppSelector((state) =>
    getCartByCustomer(state, customerId || '')
  );

  const [showToastSuccess, setShowToastSuccess] = useState({
    show: false,
    msg: '',
  });

  const [showToastError, setShowToastError] = useState({
    show: false,
    msg: '',
  });

  const { showOutOfStockTag } = useAppSelector(getCatalogConfig);

  const [uomModalState, setUomModalState] = useState<{
    open: boolean;
    line?: OrderLine;
  }>({
    open: false,
    line: undefined,
  });

  const [getOrderLevelSchemesAPI] = useGetOrderLevelSchemesMutation();
  const [promotionSchemes, setPromotionSchemes] = useState<OrderLevelScheme[]>(
    []
  );
  const [addItemToActiveOrderAPI, addItemToActiveOrderAPIResponse] =
    useAddItemToActiveOrderMutation();
  const [cartCheckoutAPI, cartCheckoutAPIResponse] =
    useCheckoutOrdersMutation();
  const [notifyBuyerApi, notifyBuyerApiResponse] = useNotifyToBuyerMutation();
  const [editScheme] = useEditOrderMutation();

  const [selectedBenefit, setSelectedBenefit] = useState<OrderLevelScheme>();
  const [showPromotionsModal, setShowPromotionsModal] = useState(false);
  const isChangingBenefitRef = useRef(false);
  const [removeOrderPromotion, removeOrderPromotionResponse] =
    useRemoveOrderPromotionMutation();

  const onSkipPromotion = () => {
    setShowPromotionsModal(false);
  };

  const onCancelPromotion = () => {
    setShowPromotionsModal(false);
  };

  const addToOffline = async () => {
    await addTaskToQueue({
      type: OfflineTaskType.PLACE_ORDER,
      customer: currentCustomer,
      payload: {},
    });
  };

  useEffect(() => {
    if (cartAPIResponse?.data && cartAPIResponse?.isSuccess) {
      const isPromotionsApplied =
        cartAPIResponse?.data?.files?.[0]?.lines?.some(
          (x) => x?.promotions?.length > 0
        );
      if (isPromotionsApplied) {
        const promotion = cartAPIResponse?.data?.files?.[0]?.lines?.find(
          (x) => x?.promotions?.length > 0
        );
        const data = {
          id: promotion?.promotionId,
          name: promotion?.promotionName,
          code: promotion?.priceListCode,
          description: '',
          schemeType: promotion?.schemeType,
          minOrderQuantity: promotion?.MOQ,
          schemeIconType: 'PRODUCTS',
          productVariantId: promotion?.productVariantId,
        };
        //@ts-ignore
        setSelectedBenefit(data);
      }
    }
  }, [cartAPIResponse]);

  const orderIds = useMemo(() => {
    const ids =
      addItemToActiveOrderAPIResponse?.data?.orders?.map(
        (order) => order?.id
      ) ||
      cartAPIResponse?.data?.files?.[0]?.lines?.map((x) => x?.orderId) ||
      [];

    const uniqueOrderIds: any[] = [
      ...new Set(ids.filter((id: number | null) => id !== null)),
    ];
    return uniqueOrderIds;
  }, [addItemToActiveOrderAPIResponse, cartAPIResponse]);

  useEffect(() => {
    if (cartData?.lines?.some((x) => x?.id)) {
      getCartDetails({
        workspaceId: sellerWorkspaceId,
        params: {
          customerId: customerId ?? currentCustomer?.customerId ?? '',
          sellerWorkspaceId,
          includeActiveOrders: true,
          includeSummary: false,
        },
      });
    }
  }, [cartData]);

  const openPromotionsModal = () => {
    setShowPromotionsModal(true);
  };

  const onApplyPromotion = (
    promotionIds: (string | number)[],
    scheme: OrderLevelScheme
  ) => {
    orderIds?.map(async (orderId) => {
      const selectedOrder = cartData?.lines?.[0];
      try {
        await editScheme({
          workspaceId: sellerWorkspaceId,
          body: {
            customerId: customerId ?? currentCustomer?.customerId ?? '',
            orderId,
            lines: selectedOrder
              ? [
                  {
                    quantity: selectedOrder?.quantity ?? 1,
                    productVariantId: selectedOrder?.productVariantId,
                    ...(typeof selectedOrder?.listPrice === 'number'
                      ? { listPrice: selectedOrder?.linePrice }
                      : {}),
                  },
                ]
              : [],
            promotionIds,
          },
        }).unwrap();
        setSelectedBenefit(scheme);
      } catch {
        return;
      }
    });
  };

  const fetchDiscounts = async () => {
    if (orderIds?.length === 0) return;
    try {
      // call all apis in parallel
      const responses = await Promise.all(
        orderIds?.map(
          (id) =>
            id &&
            getOrderLevelSchemesAPI({
              sellerWorkspaceId: sellerWorkspaceId ?? '',
              orderId: Number(id),
              customerId: customerId ?? '',
            }).unwrap()
        )
      );

      const mergedPromotions = Array.from(
        new Map(
          responses
            .flatMap(
              (response: GetOrderLevelSchemesResponse) =>
                response?.promotions || []
            )
            .map((scheme) => [scheme.id, scheme])
        ).values()
      );

      const schemes: OrderLevelScheme[] = mergedPromotions.map(
        (scheme: any) => {
          const promotionCode = scheme?.promotionCode || scheme?.code || '';
          const minOrderQuantity =
            scheme?.minQtyASC ??
            scheme?.minOrderQuantity ??
            scheme?.slab?.[0]?.from;

          return {
            id: scheme?.id,
            name: scheme?.name || 'Promotion',
            code: promotionCode,
            description: scheme?.description || '',
            schemeType:
              scheme?.schemeType || scheme?.promotionType || 'PROMOTION',
            minOrderQuantity,
            schemeIconType: scheme?.schemeIconType,
            productVariantId: scheme?.productVariantId,
          };
        }
      );
      setPromotionSchemes(schemes && schemes.length > 0 ? schemes : []);
    } catch (error) {
      console.log('getOrderLevelSchemesAPI error', error);
      setPromotionSchemes([]);
    }
  };

  useEffect(() => {
    fetchDiscounts();
  }, [orderIds, cartAPIResponse]);

  const applyPromotionOnOrder = async (scheme: OrderLevelScheme) => {
    const selectedLine =
      cartData?.lines?.find(
        (line) => line?.productVariantId === (scheme as any)?.productVariantId
      ) || cartData?.lines?.[0];
    if (
      selectedLine &&
      selectedLine?.promotionId &&
      Number(selectedLine?.promotionId) !== Number(scheme?.id) &&
      selectedLine?.orderId
    ) {
      isChangingBenefitRef.current = true;
      try {
        await removeOrderPromotion({
          workspaceId: sellerWorkspaceId,
          orderId: Number(selectedLine?.orderId),
          promotionId: Number(selectedLine?.promotionId),
        }).unwrap();
      } catch {
        isChangingBenefitRef.current = false;
        setShowPromotionsModal(false);
        return;
      }
    }
    onApplyPromotion([String(scheme.id)], scheme);
    setShowPromotionsModal(false);
  };

  useEffect(() => {
    if (showToastSuccess?.show) {
      const timeout = setTimeout(() => {
        setShowToastSuccess({
          show: false,
          msg: '',
        });
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [showToastSuccess]);

  useEffect(() => {
    if (removeOrderPromotionResponse?.isSuccess) {
      if (isChangingBenefitRef.current) {
        isChangingBenefitRef.current = false;
        return;
      }
      setSelectedBenefit(undefined);
      // callCartAPI();
      setShowToastSuccess({
        show: true,
        msg: 'Benefit removed successfully.',
      });
    } else if (removeOrderPromotionResponse?.isError) {
      if (isChangingBenefitRef.current) {
        isChangingBenefitRef.current = false;
        setShowToastError({
          show: true,
          msg: 'Unable to change benefit. Please try again.',
        });
        return;
      }
      setShowToastError({
        show: true,
        msg: 'Unable to remove benefit. Please try again.',
      });
    }
  }, [removeOrderPromotionResponse]);

  useEffect(() => {
    if (!cartData || cartData?.lines.length === 0) {
      removeTaskFromQueue({
        type: OfflineTaskType.PLACE_ORDER,
        customerId: currentCustomer?.customerId!,
      });
    }
  }, [cartData]);

  const onProductRemoveAndUpdate = (
    productVariant: ProductVariant,
    actionType?: OrderProductActionType
  ) => {
    if (actionType === OrderProductActionType.REMOVE) {
      dispatch(
        removeItemFromCart({
          customerId: currentCustomer?.customerId || '',
          line: {
            productVariantId: productVariant?.productVariantId,
          },
        })
      );
    } else if (actionType === OrderProductActionType.EDIT) {
      let oldData = cartData?.lines?.filter(
        (i) => i?.productVariantId === productVariant?.productVariantId
      )?.[0];
      dispatch(
        updateItemInCart({
          customerId: customerId || '',
          line: {
            ...oldData,
            productVariantId: productVariant?.productVariantId,
            quantity: productVariant?.newQty,
          },
        })
      );
    }
    addToOffline();
  };

  const hasAvailablePromotions = promotionSchemes.length > 0;

  const orderSummary = useMemo(() => {
    let skuCount = cartData?.lines?.length ?? 0;
    let quantity =
      cartData?.lines?.reduce((acc, i) => {
        return acc + (i?.quantity || 0);
      }, 0) ?? 0;
    let orderAmount =
      cartData?.orderAmount ||
      cartData?.lines?.reduce((acc, i) => {
        return acc + Number(i?.unitPrice) * (i?.quantity || 1);
      }, 0) ||
      0;
    let schemeSaving = 0;
    let tax = cartData?.tax || 0;
    return {
      skuCount,
      quantity,
      orderAmount,
      schemeSaving: schemeSaving,
      grossAmount: orderAmount + schemeSaving,
      tax,
      orderValue: orderAmount + tax,
    };
  }, [cartData]);

  const hasOutOfStockItem = useMemo(
    () => orderHasOutOfStockLine(cartData?.lines, !!showOutOfStockTag),
    [cartData?.lines, showOutOfStockTag]
  );

  const isSynced = useMemo(
    () =>
      cartData?.lines?.every(
        (line) => line?.id !== undefined && line?.id !== null
      ) || false,
    [cartData]
  );

  const onClickSync = async () => {
    let lines: Line[] =
      cartData?.lines?.map((i) => {
        return {
          operator: 'add',
          productVariantId: i?.productVariantId,
          quantity: i?.quantity || 1,
        };
      }) || [];
    if (lines && networkStatus) {
      lines?.length > 0 &&
        addItemToActiveOrderAPI({
          customerId: customerId || '',
          sellerWorkspaceId: sellerWorkspaceId,
          lines: lines,
          source: 'platform',
        });
    }
    await addTaskToQueue({
      type: OfflineTaskType.PLACE_ORDER,
      customer: currentCustomer,
      payload: { itemsSynced: true },
    });
  };

  const onClickCheckout = () => {
    if (cartData?.poFileId && networkStatus) {
      cartCheckoutAPI({
        customerId: customerId || '',
        sellerWorkspaceId: sellerWorkspaceId,
        poFileIds: [cartData?.poFileId],
      });
    }
  };

  const onNotifyBuyer = () => {
    if (cartData?.poFileId && networkStatus) {
      notifyBuyerApi({
        sellerWorkspaceId: sellerWorkspaceId,
        poFileId: cartData?.poFileId,
        customerId: customerId || '',
      });
    }
  };

  useEffect(() => {
    if (addItemToActiveOrderAPIResponse?.isSuccess) {
      const orders = addItemToActiveOrderAPIResponse?.data?.orders || [];

      if (!orders.length) return;

      const merged = orders.reduce(
        (acc, order) => {
          acc.orderAmount += Number(order?.suggestedValueWithTax || 0);
          acc.tax += Number(order?.taxAmount || 0);

          if (order?.orderLine?.length) {
            acc.lines.push(...order.orderLine);
          }

          if (order?.pofileId) {
            acc.poFileIds.add(order.pofileId);
          }

          return acc;
        },
        {
          orderAmount: 0,
          tax: 0,
          lines: [] as any[],
          poFileIds: new Set<string>(),
          orderIds: new Set<string>(),
        }
      );

      const orderIds = addItemToActiveOrderAPIResponse?.data?.orders?.map(
        (order) => order?.id
      );

      dispatch(
        addBulkItemsToCart({
          customerId: customerId || '',
          orderAmount: merged.orderAmount,
          tax: merged.tax,
          lines: merged.lines,
          orderIds: orderIds,
          poFileId: [...merged.poFileIds][0],
        })
      );

      dispatch(
        showToast({
          message: 'Cart Synced Successfully',
          type: ErrorVariants.SUCCESS,
          canDisplay: true,
          responseStatus: 'Success',
        })
      );
    }
  }, [addItemToActiveOrderAPIResponse]);

  useEffect(() => {
    if (cartCheckoutAPIResponse?.isSuccess && customerId) {
      dispatch(clearCartByCustomer(customerId));
      dispatch(
        showToast({
          message: 'Cart Checked out Successfully',
          type: ErrorVariants.SUCCESS,
          canDisplay: true,
          responseStatus: 'Success',
        })
      );
      navigate(`/customers/profile/${customerId}`);
    }
  }, [cartCheckoutAPIResponse]);

  useEffect(() => {
    if (notifyBuyerApiResponse?.isSuccess) {
      dispatch(
        showToast({
          message: 'Buyer has been notified successfully',
          type: ErrorVariants.SUCCESS,
          canDisplay: true,
          responseStatus: 'Success',
        })
      );
    }
  }, [notifyBuyerApiResponse]);

  const onOpenUomModal = (cartLine: any) => {
    setUomModalState({ open: true, line: cartLine });
  };

  const onCloseUomModal = () => {
    setUomModalState({ open: false, line: undefined });
  };

  const onUomChange = (uom: UOM) => {
    const lineItem = uomModalState.line;
    if (lineItem && lineItem.productVariant?.id) {
      dispatch(
        updateItemInCart({
          customerId: customerId!,
          line: {
            ...lineItem,
            promotionId: lineItem?.promotionIds?.[0],
            promotionName: lineItem?.promotionNames?.[0],
            quantity: Number(lineItem.quantity) * uom.packSize,
            productVariantId: lineItem.productVariant?.id,
          },
        })
      );
      onCloseUomModal();
    }
  };

  return (
    <>
      <div className="offline-cart-content">
        {isEmpty(cartData?.lines) ? (
          <EmptyStateView
            imageSrc={SVGS.NoCartItems}
            message="Your cart is empty"
            description="Start adding products to place your order"
          />
        ) : (
          <>
            <div className="offline-cart-content-header">
              <img src={SVGS.Products} alt="products-icon" />
              <span className="offline-cart-content-header-text font-semibold">
                {cartData?.lines?.length} item
                {cartData?.lines?.length || 0 > 1 ? 's' : ''}
              </span>
            </div>

            <div className="offline-cart-content-wrapper">
              <div className="offline-cart-content-wrapper-products">
                {cartData?.lines?.map((productInCart, index) => {
                  const product: ProductVariant = {
                    id: productInCart.productVariantId,
                    productVariantId: productInCart.productVariantId,
                    name: productInCart.skuName || '',
                    shortName: productInCart.skuName || '',
                    shortDescription: productInCart.description || '',
                    sku: productInCart.sku || '',
                    price: Number(productInCart.unitPrice),
                    basePrice: Number(productInCart.unitPrice),
                    MRP: Number(productInCart.unitPrice),
                    PTR: Number(productInCart.unitPrice),
                    quantity: productInCart.quantity || 1,
                    newQty: productInCart.quantity,
                    minOrderQty: productInCart.minOrderQty || 1,
                    baseUom: productInCart.productVariant?.baseUom || '',
                    additionalUOM:
                      productInCart.productVariant?.additionalUOM || '',
                    packSize: productInCart.productVariant?.packSize || 0,
                    categories: productInCart.categories || [],
                    segments: productInCart.segments || [],
                    facets: productInCart.facets || [],
                    enabled: true,
                    taxCategory: {} as any,
                    taxCategoryId: 0,
                    productId: 0,
                    parentProductId: 0,
                    sortOrder: 0,
                    warrentyInformation: '',
                    condition: '',
                    qtyMultiplier: 1,
                    division: [],
                    cfas: [],
                    collections: [],
                    promotions: [],
                    stock: (productInCart as { stock?: number })?.stock,
                    assets: [],
                  };
                  return (
                    <CustomerProductCard
                      key={`${product?.id}-${index}`}
                      data={product}
                      handleUpdate={onProductRemoveAndUpdate}
                      isInCart={true}
                      openUomModal={() => onOpenUomModal(productInCart)}
                      showOutOfStockTag={showOutOfStockTag}
                    />
                  );
                })}
              </div>

              {!selectedBenefit && hasAvailablePromotions && (
                <div
                  className="order-details-footer-apply-benefits font-semibold"
                  onClick={openPromotionsModal}
                >
                  <img src={PNGS.SchemePercentIcon} alt="" />
                  <span>Apply Benefits</span>
                  <ArrowRight color="#0067FF" />
                </div>
              )}

              {selectedBenefit && (
                <div className="order-details-selected-benefit">
                  <div className="order-details-selected-benefit-content">
                    <img src={PNGS.ManualOfferIcon} alt="benefit" />
                    <div className="order-details-selected-benefit-content-text">
                      <span className="font-semibold">
                        {selectedBenefit?.name}
                      </span>
                      {selectedBenefit.description ? (
                        <span className="font-regular">
                          {selectedBenefit?.description}
                        </span>
                      ) : null}
                      <span className="order-details-selected-benefit-meta font-regular">
                        {selectedBenefit?.code}
                        {selectedBenefit?.minOrderQuantity
                          ? ` • Min Order Quantity: ${selectedBenefit?.minOrderQuantity}`
                          : ''}
                      </span>
                      <button
                        className="order-details-selected-benefit-remove font-semibold"
                        onClick={openPromotionsModal}
                      >
                        Change benefit
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div style={{ margin: 10, marginBottom: 0 }}>
                <OrderSummaryCard
                  orderSummary={orderSummary}
                  title="Order Summary"
                />
              </div>
            </div>

            {/* BILL SUMMARY */}

            {/* FOOTER */}
            <CartSyncAndCheckout
              isSynced={isSynced}
              hasOutOfStockItem={hasOutOfStockItem}
              onClickSync={onClickSync}
              onClickCheckout={onClickCheckout}
              addItemToActiveOrderAPIResponse={addItemToActiveOrderAPIResponse}
              cartCheckoutAPIResponse={cartCheckoutAPIResponse}
              onNotifyBuyer={onNotifyBuyer}
              notifyBuyerApiResponse={notifyBuyerApiResponse}
            />
          </>
        )}
      </div>
      <SelectUOMDrawer
        open={uomModalState.open}
        onClose={onCloseUomModal}
        onUomChange={onUomChange}
        currentLine={uomModalState.line}
      />
      {showPromotionsModal && (
        <PromotionsModal
          schemes={promotionSchemes}
          selectedBenefit={selectedBenefit}
          onApply={applyPromotionOnOrder}
          onSkip={() => {
            onSkipPromotion();
          }}
          onCancel={onCancelPromotion}
          isApplying={false}
          modalTitle={
            selectedBenefit ? 'Change Benefit' : 'Cash Discount Benefit'
          }
        />
      )}
    </>
  );
};
